<template>
  <el-popover
    v-model:visible="popoverVisible"
    trigger="click"
    placement="bottom-start"
    popper-class="paged-popver"
    v-loading="is_loading"
    width="360"
    @close="
      editMode = false;
      newPageModal = false;
    "
  >
    <!-- 🔴 reference -->
    <template #reference>
      <el-input v-model="selectedPageLabel" readonly class="outside-input" :title="selectedPageLabel">
        <template #suffix>
          <svg-icon
            name="arrow"
            :class="{ 'arrow-open': popoverVisible }"
            class="select-arrow"
            @click="popoverVisible = !popoverVisible"
          />
        </template>
      </el-input>
    </template>

    <div class="pover-header">
      <h3>{{ title }}</h3>
    </div>

    <drop-list class="page-list" :items="pages" @reorder="handleReorder">
      <template #item="{ item, index }">
        <drag
          :key="item.id"
          :scrolling-edge-size="-1000"
          handle=".drag-handle"
          @dragstart="dragStart(index, item)"
          @dragend="dragEnd(index, item)"
        >
          <div class="page-row clickable">
            <span class="drag-handle">⋮⋮</span>

            <div class="page-row-item" @click="handleRowClick(item)">
              <el-checkbox
                v-if="!editMode"
                :model-value="selectedPageId === item.id"
              >
                {{ item.name }}
              </el-checkbox>

              <el-checkbox
                v-else-if="editingItemId !== item.id"
                v-model="item.checked"
                :disabled="item.isHome"
                @click.stop
              >
                {{ item.name }}
              </el-checkbox>

              <el-input
                v-if="editMode && editingItemId === item.id"
                v-model="editingText"
                size="small"
                @keyup.enter="saveEdit(item)"
                @keyup.escape="cancelEdit"
                @click.stop
              />
            </div>

            <span v-if="item.isHome" class="default-tag"> Default </span>

            <svg-icon
              v-if="editMode && editingItemId !== item.id"
              class="edit-icon"
              name="pen"
              @click.stop="openEdit(item)"
            />

            <div v-if="editMode && editingItemId === item.id" class="edit-actions" @click.stop>
              <svg-icon class="action-icon cancel-icon" name="close" @click.stop="cancelEdit" />
              <svg-icon class="action-icon confirm-icon" name="yes-2" @click.stop="saveEdit(item)" />
            </div>
          </div>
        </drag>
      </template>

      <template v-slot:feedback>feedback</template>
    </drop-list>

    <div class="popover-footer">
      <el-button size="small" v-if="!editMode" @click="newPageModal = true" :disabled="pages.length >= 5">
        <svg-icon name="plus" />Add ({{ pages.length }}/5)
      </el-button>
      <el-button size="small" v-if="!editMode" @click="editMode = true"> Edit </el-button>

      <template v-else>
        <el-button type="primary" :class="{ active: editMode }" @click="editMode = false"> Done </el-button>

        <el-button
          type="danger"
          :disabled="!hasCheckedItems"
          :class="{ active: hasCheckedItems }"
          @click="deletePageModal = true"
        >
          Delete
        </el-button>
      </template>
    </div>

    <el-dialog class="add-page-dialog" v-model="newPageModal" width="350px">
      <div>
        <label>New page name:</label>
        <el-input v-model="newPageName" placeholder="Home" maxlength="50" />
      </div>
      <template #footer>
        <el-button
          @click="
            newPageModal = false;
            newPageName = '';
          "
          >Cancel</el-button
        >
        <el-button type="primary" @click="savePage(1)" :disabled="!newPageName">Confirm</el-button>
      </template>
    </el-dialog>

    <el-dialog
      class="delete-page-dialog"
      v-model="deletePageModal"
      width="400px"
      :show-close="false"
      :show-header="false"
    >
      <h3>Are you sure you want to continue?</h3>
      <div>Articles in this page will be move to the default page unless they are moved out first.</div>
      <template #footer>
        <el-button @click="deletePageModal = false">Cancel</el-button>
        <el-button type="danger" @click="deleteSelected">Continue</el-button>
      </template>
    </el-dialog>
  </el-popover>
</template>
<script setup>
import emitter from '@/utils/EventBus';
import { ref, onMounted, onUnmounted, computed, getCurrentInstance, watch, h } from 'vue';
import { ElInput, ElPopover, ElButton, ElRadio, ElCheckbox, ElDialog } from 'element-plus';
import { useStore } from 'vuex';
import { Drag, DropList } from 'vue-easy-dnd';
import { useNotification } from '@/components/ui/notification';

const emit = defineEmits(['changePage']);
const props = defineProps({
  initValue: {
    type: String,
    default: 'Home',
  },
  allPages: {
    type: Array,
    default: () => [],
  },
  item: {
    type: Object,
    default: () => ({}),
  },
  needWait: {
    type: Boolean,
    default: false,
  },
});

const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const store = useStore();
const { openNotBottom } = useNotification();

const pages = ref([]);

const selectedPageId = ref(1);
const editMode = ref(false);
const popoverVisible = ref(false);
const editingItemId = ref(null);
const editingText = ref('');
let drag_pre_index = -1;
const is_sorting = ref(false);
const is_update_data = ref(false);
const is_loading = ref(false);

const title = computed(() => {
  return editMode.value ? 'Edit pages' : 'Select or add pages';
});

const selectedPageLabel = computed(() => {
  const selectedPage = pages.value.find((p) => p.id === selectedPageId.value);
  return selectedPage ? selectedPage.name : '';
});

//dialog
const newPageModal = ref(false);
const newPageName = ref('');

const deletePageModal = ref(false);

// Initialize selectedPageId based on initValue
watch(
  () => props.allPages,
  (newVal) => {
    if (Array.isArray(newVal) && newVal.length > 0) {
      pages.value = newVal.map((p) => ({
        id: p.id,
        name: p.title,
        isHome: p.is_default_page || false,
        checked: false,
      }));

      if (props.initValue) {
        const page = pages.value.find((p) => p.name === props.initValue);
        if (page) {
          selectedPageId.value = page.id;
        }
      }
    }
  },
  { immediate: true }
);
//+++++++++++++++++++++
//mounted
//+++++++++++++++++++++
const dragStart = (index, item) => {
  // log('blue', 'Drag start', index);
  drag_pre_index = index;
  is_sorting.value = true;
};

const handleReorder = (event) => {
  // vue-easy-dnd reorder event handler
  if (event && event.apply) {
    event.apply(pages.value);
  }
};

const dragEnd = (index, item) => {
  is_sorting.value = false;
  console.log('blue', 'dragEnd', item,index);

  if (index !== drag_pre_index) {
    is_update_data.value = true;
    savePage(2, {new_index: index, id: item.id});
    is_update_data.value = false;
  }

  drag_pre_index = -1;
};

const handleRowClick = (item) => {
  if (!editMode.value) {
    //picking and save

    if (props.needWait) {
      selectedPageId.value = item.id;
      emit('changePage', item);
    } else {
      is_loading.value = true;
      let formData = new FormData();
      formData.append('creator_academy_page_id', item.id);
      formData.append('creator_academy_article_id', props.item.id);

      ajax
        .post('/creator-academy/update-published-article-page', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((res) => {
          selectedPageId.value = item.id;
          openNotBottom('Page selected successfully.', 'success');
          is_loading.value = false;
        })
        .catch((err) => {
          openNotBottom('Failed to select page. Please try again.', 'error');
          is_loading.value = false;
        });

      return;
    }
  }

  if (editMode.value && !item.isHome) {
    item.checked = !item.checked;
  }
};

const hasCheckedItems = computed(() => pages.value.some((p) => p.checked && !p.isHome));

const openEdit = (item) => {
  editingItemId.value = item.id;
  editingText.value = item.name;
};

const saveEdit = (item) => {
  savePage(3, item);
};

const cancelEdit = () => {
  editingItemId.value = null;
  editingText.value = '';
};

const deleteSelected = () => {
  const fromData = new FormData();

  // Collect checked page IDs (excluding Home page)
  const checkedIds = pages.value.filter((p) => p.checked && !p.isHome).map((p) => p.id);

  // Append each ID to the FormData with the array key
  checkedIds.forEach((id) => {
    fromData.append('creator_academy_page_ids[]', id);
  });

  ajax
    .post('/creator-academy/delete-page', fromData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((res) => {
      // Remove deleted pages from the list
      pages.value = pages.value.filter((p) => !p.checked || p.isHome);
      deletePageModal.value = false;
      openNotBottom('Page deleted and articles updated accordingly.', 'success');
      emitter.emit('refresh-academy-page-list');
    })
    .catch((err) => {
      deletePageModal.value = false;
      openNotBottom('Failed to delete page. Please try again.', 'error');
    });
};

const savePage = (type, item) => {
  // type===1 => create, type===2 => sort, type===3 => edit name
  // All types use the same API endpoint
  const fromData = new FormData();

  if (type === 1) {
    // Create new page - only send 'title'
    fromData.append('title', newPageName.value);
  } else if (type === 2) {
    // Sort pages - send 'new_sort_order' and 'creator_academy_page_id'
    fromData.append('new_sort_order', Number(item.new_index + 1));
    fromData.append('creator_academy_page_id', item.id);
  } else if (type === 3) {
    // Edit page name - send 'title' and 'creator_academy_page_id'
    fromData.append('title', editingText.value);
    fromData.append('creator_academy_page_id', item.id);
  }

  ajax
    .post('/creator-academy/save-page', fromData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((res) => {
      const data = res.data.data;
      if (type === 1) {
        pages.value.push({
          id: data.id,
          name: newPageName.value,
          checked: false,
        });
        newPageName.value = '';
        newPageModal.value = false;
        openNotBottom('Page added successfully.', 'success');
      } else if (type === 2) {
        openNotBottom('Page order updated successfully.', 'success');
      } else if (type === 3) {
        item.name = editingText.value;
        editingItemId.value = null;
        editingText.value = '';
        openNotBottom('Page name updated successfully.', 'success');
      }
      emitter.emit('refresh-academy-page-list');
    })
    .catch((err) => {
      console.error('savePage err', err);
      const action = type === 1 ? 'add' : type === 2 ? 'sort' : 'update';
      openNotBottom(`Failed to ${action} page. Please try again.`, 'error');
    });
};

onMounted(() => {});

onUnmounted(() => {});
</script>
<style lang="scss">
.outside-input {
  .el-input__wrapper,
  input {
    cursor: pointer;
  }

  .el-input__suffix {
    .select-arrow {
      width: 12px !important;
      height: 12px !important;
      transition: transform 0.3s ease;
    }

    .arrow-open {
      transform: rotate(180deg);
    }
  }
}

.paged-popver {
  padding: 0 !important;

  svg {
    width: 14px;
    height: 14px;
  }

  .pover-header {
    padding: 8px;

    h3 {
      font: var(--font-med-12);
    }
  }

  .page-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 2px 12px;
    cursor: pointer;
    font: var(--font-med-12);
  }

  .page-row.clickable:hover {
    background: var(--black-10);
  }

  .page-row-item {
    flex: 1;
  }

  .dragging {
    background: var(--black-10);
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .drag-handle {
    cursor: grab;
    user-select: none;
    color: var(--black-75);
  }

  .dnd-drag {
    transition: transform 0.2s ease;
    .drag-handle {
      cursor: grab;
    }
  }

  .default-tag {
    margin-left: auto;
    font-size: 12px;
    color: var(--black-50);
  }

  .edit-icon {
    margin-left: 8px;
    cursor: pointer;
    width: 12px;
    height: 12px;
  }

  .edit-actions {
    display: flex;
    gap: 8px;
    margin-left: auto;

    .action-icon {
      cursor: pointer;
      width: 14px;
      height: 14px;
      transition: color 0.2s ease;

      &.confirm-icon {
        color: var(--color-success, #67c23a);

        &:hover {
          color: var(--color-success-dark, #529b2e);
        }
      }

      &.cancel-icon {
        color: var(--color-danger, #f56c6c);

        &:hover {
          color: var(--color-danger-dark, #dd001b);
        }
      }
    }
  }

  .popover-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px;
  }

  .el-button {
    margin: 0;
    svg {
      width: 10px;
      height: 10px;
    }

    span {
      display: flex;
      gap: 4px;
      align-items: center;
    }
  }

  .el-dialog__footer {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
  .add-page-dialog {
    .el-dialog__header {
      display: none;
    }

    label {
      font: var(--font-med-16);
      color: var(--black-90);
    }

    .el-input {
      margin-top: 4px;
    }
  }
}

.delete-page-dialog {
  .el-dialog__header {
    display: none;
  }
  h3 {
    font: var(--font-demi-20);
    margin-bottom: 8px;
  }
  div {
    font-size: 16px;
    color: var(--text-secondary);
  }
}
</style>
